import axios from "axios";

export const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API,
  withCredentials: true,
  // Prevent infinite spinners on flaky mobile networks — after 30s any
  // request auto-aborts so the UI can show an error / retry state.
  timeout: 30000,
});

api.interceptors.request.use((config) => {
  const t = localStorage.getItem("akb_token");
  if (t) config.headers.Authorization = `Bearer ${t}`;
  return config;
});

// -----------------------------------------------------------------------------
// v4.11.4 Cloudflare 520 / gateway retry
// -----------------------------------------------------------------------------
// Cloudflare 520/521/522/524 = origin/edge sorunu. Bunlar genelde ANLIK olur
// (worker restart, cold start, geçici bağlantı). Kullanıcıyı sıkmadan
// 2 defa 800 ms aralıklarla otomatik yeniden dene — başarısız kalırsa hatayı
// göster. GET/HEAD idempotent olduğu için güvenle retry edilebilir; POST/PUT/
// DELETE için sadece SUNUCU HİÇ ULAŞILAMADIYSA (Network Error) retry yaparız.
const GATEWAY_ERR_CODES = [502, 503, 504, 520, 521, 522, 523, 524];
const RETRY_MAX = 2;
const RETRY_DELAY_MS = 800;

api.interceptors.response.use(
  (r) => r,
  async (err) => {
    const cfg = err.config || {};
    const status = err.response?.status;
    const method = (cfg.method || "get").toLowerCase();
    const isGatewayErr = GATEWAY_ERR_CODES.includes(status);
    const isNetworkErr = !err.response && err.code !== "ERR_CANCELED";
    const isIdempotent = ["get", "head", "options"].includes(method);

    // Sadece güvenli metodlarda auto-retry — POST/PUT/DELETE yalnızca gerçek
    // network error'da (sunucuya ulaşılamadı) retry olsun ki double-write olmasın.
    const shouldRetry =
      (isGatewayErr && isIdempotent) || (isNetworkErr && isIdempotent);

    if (shouldRetry) {
      cfg.__retryCount = (cfg.__retryCount || 0) + 1;
      if (cfg.__retryCount <= RETRY_MAX) {
        await new Promise((res) => setTimeout(res, RETRY_DELAY_MS * cfg.__retryCount));
        return api(cfg);
      }
    }
    return Promise.reject(err);
  }
);

// -----------------------------------------------------------------------------
// PERF v3.5.6: Stale-while-revalidate for GET requests — refined.
// -----------------------------------------------------------------------------
// Small responses (<40 KB serialized) are cached in sessionStorage so that
// re-visiting a page renders from cache instantly while the background fetch
// revalidates. Larger responses SKIP the cache because JSON.stringify of a
// 200 KB payload is a 100-200 ms main-thread block that actually makes
// navigation feel jankier than just re-fetching over gzip'd HTTP. Concurrent
// dedupe (inflight Map) still applies so parallel components asking for the
// same URL only trigger one network call.
const SWR_KEY_PREFIX = "akb_swr:";
const SWR_TTL_MS = 5 * 60 * 1000;
const SWR_MAX_CACHE_BYTES = 40_000; // ~40 KB serialized
const _inflight = new Map(); // url → Promise (dedupe concurrent identical GETs)

function _swrKey(cfgUrl, params) {
  const p = params ? "?" + new URLSearchParams(params).toString() : "";
  return SWR_KEY_PREFIX + cfgUrl + p;
}

/** GET with stale-while-revalidate.
 *  onCached: optional callback fired synchronously with the cached body BEFORE
 *  the network response arrives, so the UI can render immediately.
 */
export async function swrGet(url, { params, onCached } = {}) {
  const key = _swrKey(url, params);
  let cached = null;
  try {
    const raw = sessionStorage.getItem(key);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed && Date.now() - parsed.t < SWR_TTL_MS) {
        cached = parsed.d;
      }
    }
  } catch { /* ignore parse errors */ }
  if (cached && typeof onCached === "function") {
    try { onCached(cached); } catch { /* ignore */ }
  }

  const inflightKey = url + (params ? JSON.stringify(params) : "");
  if (_inflight.has(inflightKey)) return _inflight.get(inflightKey);

  const p = api.get(url, { params }).then((res) => {
    // Only cache small payloads to avoid main-thread jank on serialize.
    try {
      const s = JSON.stringify(res.data);
      if (s.length <= SWR_MAX_CACHE_BYTES) {
        sessionStorage.setItem(key, JSON.stringify({ t: Date.now(), d: res.data }));
      } else {
        // Big payload — drop any stale cache so we don't hold on to gigabytes
        sessionStorage.removeItem(key);
      }
    } catch {
      /* quota exceeded or non-serializable — best effort */
    }
    _inflight.delete(inflightKey);
    return res;
  }).catch((err) => {
    _inflight.delete(inflightKey);
    if (cached != null) return { data: cached, _fromCache: true };
    throw err;
  });
  _inflight.set(inflightKey, p);
  return p;
}

/** Invalidate cache entries whose key starts with the given path prefix. Call
 *  after mutations (POST/PUT/DELETE) so the next swrGet fetches fresh. */
export function invalidateSwr(pathPrefix) {
  try {
    const target = SWR_KEY_PREFIX + pathPrefix;
    const keys = [];
    for (let i = 0; i < sessionStorage.length; i++) {
      const k = sessionStorage.key(i);
      if (k && k.startsWith(target)) keys.push(k);
    }
    keys.forEach((k) => sessionStorage.removeItem(k));
  } catch { /* ignore */ }
}

export default api;

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Bir hata oluştu.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}
